import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Skeleton, Tabs, Avatar, Typography, Col } from 'antd';
import PageHeader from '../../components/PageHeader/PageHeader';
import moment from 'moment';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import ContentCustom from '../../components/ContentCustom/ContentCustom';
import DescriptionList from '../../components/DescriptionList/DescriptionList';
import { daysOfWeek } from '../../utils/constants/misc';

const { TabPane } = Tabs;

const CustomerContacts = () => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [insurerDetails, setInsurerDetails] = useState([]);
  const [current, setCurrent] = useState(0);
  const { message } = useErrorMessage();

  const getInsurerDetails = async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', { url: '/insurers/details' });
      setInsurerDetails(data);
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      await getInsurerDetails();
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <PageHeader title={t('contacts.title')} />
      <ContentCustom>
        {!!insurerDetails.length > 1 && (
          <Tabs onChange={(key) => setCurrent(key)}>
            {insurerDetails.map(({ insurer }, idx) => (
              // eslint-disable-next-line
              <TabPane tab={insurer.name || 'N/C'} key={idx} />
            ))}
          </Tabs>
        )}
        <Row style={{ marginBottom: 24 }}>
          <Skeleton
            loading={isLoading}
            title={0}
            paragraph={0}
            avatar={{ size: 88 }}
          >
            <Avatar
              size={88}
              src={insurerDetails[current]?.insurer?.photo || ''}
            >
              {insurerDetails[current]?.insurer?.name &&
                `${insurerDetails[current]?.insurer?.name?.[0]}`}
            </Avatar>
          </Skeleton>
        </Row>
        <Row>
          <Skeleton loading={isLoading} paragraph={{ rows: 5 }}>
            {insurerDetails[current]?.insurer?.name && (
              <Typography.Title level={4}>
                {insurerDetails[current]?.insurer?.name}
              </Typography.Title>
            )}
            <DescriptionList
              data={[
                {
                  name: 'address',
                  label: t('contacts.customer.insurer.address'),
                  content: (
                    <>
                      {`${
                        insurerDetails[current]?.insurer?.address?.number || ''
                      } ${
                        insurerDetails[current]?.insurer?.address?.street || ''
                      }`}
                      {insurerDetails[current]?.insurer?.address
                        ?.additionnal && (
                        <>
                          <br />
                          {insurerDetails[current].insurer.address.additionnal}
                        </>
                      )}
                      <br />
                      {`${
                        insurerDetails[current]?.insurer?.address
                          ?.postal_code || ''
                      } ${
                        insurerDetails[current]?.insurer?.address?.city || ''
                      }`}
                    </>
                  ),
                  span: 3
                },
                {
                  name: 'phone',
                  label: t('contacts.customer.insurer.phone_number'),
                  content: insurerDetails[current]?.insurer?.phone_number && (
                    <a
                      href={`tel:${insurerDetails[current]?.insurer?.phone_number}`}
                    >
                      {insurerDetails[current]?.insurer?.phone_number.replace(
                        /(.{2})/g,
                        '$1 '
                      )}
                    </a>
                  ),
                  span: 1
                },
                {
                  name: 'mail',
                  label: t('contacts.customer.insurer.email'),
                  content: insurerDetails[current]?.insurer?.email && (
                    <a
                      href={`mailto:${insurerDetails[current]?.insurer?.email}`}
                    >
                      {insurerDetails[current]?.insurer?.email}
                    </a>
                  ),
                  span: 2
                }
              ]}
            />
          </Skeleton>
        </Row>
        {insurerDetails[current]?.insurer?.open_hours && (
          <>
            <Typography.Title level={4}>
              Heures d&apos;ouverture
            </Typography.Title>
            <ContentCustom
              style={{
                padding: 20,
                backgroundColor: 'var(--componentBackground)',
                borderRadius: 10,
                marginBottom: 24
              }}
            >
              <Skeleton loading={isLoading} paragraph={{ rows: 5 }}>
                <Row>
                  {daysOfWeek.map((day) => (
                    <Col xs={{ span: 24 }} lg={{ span: 4 }} key={day}>
                      <Row justify="center">
                        <Col>
                          <Typography.Text strong>
                            {t(`global.week_days.${day}`)}
                          </Typography.Text>
                        </Col>
                      </Row>
                      <Row justify="center">
                        <Col>
                          {insurerDetails[current]?.insurer?.open_hours?.[
                            day
                          ] &&
                          !!insurerDetails[current]?.insurer?.open_hours?.[day]
                            .length
                            ? `${moment(
                                insurerDetails[current]?.insurer?.open_hours?.[
                                  day
                                ][0]
                              ).format('HH:mm')} - ${moment(
                                insurerDetails[current]?.insurer?.open_hours?.[
                                  day
                                ][1]
                              ).format('HH:mm')}`
                            : 'N/A'}
                        </Col>
                      </Row>
                    </Col>
                  ))}
                </Row>
              </Skeleton>
            </ContentCustom>
          </>
        )}
        {insurerDetails[current]?.insurer?.products &&
          !!insurerDetails[current].insurer.products.length && (
            <>
              <Typography.Title level={4}>
                Produits proposés par votre assureur
              </Typography.Title>
              <ContentCustom
                style={{
                  padding: 20,
                  backgroundColor: 'var(--componentBackground)',
                  borderRadius: 10
                }}
              >
                <Skeleton loading={isLoading} paragraph={{ rows: 5 }}>
                  {insurerDetails[current].insurer.products.map((product) => (
                    <Typography.Paragraph key={product}>
                      {product}
                    </Typography.Paragraph>
                  ))}
                </Skeleton>
              </ContentCustom>
            </>
          )}
      </ContentCustom>
    </>
  );
};

export default CustomerContacts;
